import { recordEvent } from "app/analytics";
import { AppPlatform, AnalyticsEventType } from "app/types";
import { PlanCategory, BillingPeriod } from "./useNativePackages";

export enum PurchaseStore {
  APP_STORE = "app_store",
  PLAY = "play",
  STRIPE = "stripe",
}

// ネイティブから届く購入の結果（Command subscription の data.result）
export enum NativePurchaseResult {
  SUCCESS = "success",
  CANCELLED = "cancelled",
  FAILED = "failed",
}

type PurchaseParams = {
  plan_category: PlanCategory;
  billing_period: BillingPeriod;
  store: PurchaseStore;
  source: string;
};

// Stripe へ移ってから戻るまで、始めた購入の中身を持ち越す
const CHECKOUT_STORAGE_KEY = "analytics_pending_checkout";

// 始めた購入。結果を受け取る処理は画面ごとに複数あるので、最初に受け取った 1 つだけが結果を送る
let pending: PurchaseParams | null = null;

export const getPurchaseStore = (platform: AppPlatform): PurchaseStore => {
  if (platform === AppPlatform.iOS) return PurchaseStore.APP_STORE;
  if (platform === AppPlatform.Android) return PurchaseStore.PLAY;
  return PurchaseStore.STRIPE;
};

const readCheckout = (): PurchaseParams | null => {
  try {
    const value = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    return value ? (JSON.parse(value) as PurchaseParams) : null;
  } catch {
    return null;
  }
};

const clearCheckout = () => {
  try {
    sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
  } catch {
    // sessionStorage が使えないときは何もしない
  }
};

export const recordPurchaseStart = (params: PurchaseParams) => {
  pending = params;
  if (params.store === PurchaseStore.STRIPE) {
    try {
      sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(params));
    } catch {
      // 戻ったときの結果が送れないだけなので、購入は続ける
    }
  }
  recordEvent(AnalyticsEventType.PURCHASE_START, params);
};

export const recordPurchaseResult = (
  type:
    | AnalyticsEventType.PURCHASE_COMPLETE
    | AnalyticsEventType.PURCHASE_CANCEL
    | AnalyticsEventType.PURCHASE_ERROR,
  errorCode?: string,
) => {
  const params = pending ?? readCheckout();
  pending = null;
  clearCheckout();
  if (!params) return;
  recordEvent(type, errorCode ? { ...params, error_code: errorCode } : params);
};

/** ネイティブの購入の結果を送る。受け取った結果の種類を返す */
export const recordNativePurchaseResult = (data?: {
  result?: string;
  error_code?: string;
}): NativePurchaseResult => {
  if (data?.result === NativePurchaseResult.SUCCESS) {
    recordPurchaseResult(AnalyticsEventType.PURCHASE_COMPLETE);
    return NativePurchaseResult.SUCCESS;
  }
  if (data?.result === NativePurchaseResult.CANCELLED) {
    recordPurchaseResult(AnalyticsEventType.PURCHASE_CANCEL);
    return NativePurchaseResult.CANCELLED;
  }
  recordPurchaseResult(AnalyticsEventType.PURCHASE_ERROR, data?.error_code ?? "failed");
  return NativePurchaseResult.FAILED;
};

/**
 * Stripe から戻ったとき（success_url には session_id が付く。cancel_url とブラウザの戻るには付かない）。
 * Stripe へ移る前に始めた購入がなければ何もしない。
 */
export const recordCheckoutReturn = () => {
  if (!readCheckout()) return;
  const completed = new URLSearchParams(window.location.search).has("session_id");
  recordPurchaseResult(
    completed ? AnalyticsEventType.PURCHASE_COMPLETE : AnalyticsEventType.PURCHASE_CANCEL,
  );
};
